import { VariablesOf, graphql } from "@/config/graphql";
import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

const Content = styled(Box)`
  padding: 16px 24px;
`;
const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const getFuelControlOptionsQuery = graphql(`
  query getFuelControlOptions {
    fuelControlOptions {
      name
      codes
    }
  }
`);
const addFuelControlDetailMutation = graphql(`
  mutation addFuelControlDetail($params: AddFuelControlDetailParams!) {
    addFuelControlDetail(params: $params) {
      nextModified
      report {
        id
        fuelControlDetails {
          id
        }
      }
    }
  }
`);
const removeFuelControlDetailMutation = graphql(`
  mutation removeFuelControlDetail($params: RemoveFuelControlDetailParams!) {
    removeFuelControlDetail(params: $params) {
      nextModified
      report {
        id
        fuelControlDetails {
          id
        }
      }
    }
  }
`);
const updateFuelControlDetailMutation = graphql(`
  mutation updateFuelControlDetail($params: UpdateFuelControlDetailParams!) {
    updateFuelControlDetail(params: $params) {
      nextModified
      report {
        id
        fuelControlDetails {
          id
          performance
          __typename
          reportId
          resourceName
          resourceDetail
          initialHourometer
          finalHourometer
          diesel
          dieselHourometer
          dieselLastHourometer
          hours
        }
      }
    }
  }
`);

const fuelControlDetailBaseSchemaObject = z.object({
  resourceDetail: z.string().optional(),
  resourceName: z.string().min(1, "No puede estar vacío"),
  id: z.coerce.number().finite().optional(),
  type: z.string().optional(),
  dieselHourometer: z.coerce
    .number({
      invalid_type_error: "Debes ingresar el horometro de carga del diesel",
    })
    .finite()
    .gte(0, "No puede ser negativo")
    .optional(),
  dieselLastHourometer: z.coerce
    .number({
      invalid_type_error: "Debes ingresar el último horometro de carga",
    })
    .finite()
    .gte(0, "No puede ser negativo")
    .optional(),
  performance: z.preprocess(
    (val) => (!val ? 0 : Number(val)),
    z.number().finite()
  ),
  hours: z.preprocess((val) => (!val ? 0 : Number(val)), z.number().finite()),
});
const onlyHourometerSchemaObject = z.object({
  finalHourometer: z.coerce
    .number({ invalid_type_error: "Debes ingresar el valor del horometro" })
    .finite()
    .gte(0, "No puede ser negativo"),
  initialHourometer: z.coerce
    .number({ invalid_type_error: "Debes ingresar el valor del horometro" })
    .finite()
    .gte(0, "No puede ser negativo"),
});
const onlyDieselSchemaObject = z.object({
  diesel: z.coerce
    .number({ invalid_type_error: "Debes ingresar el diesel utilizado" })
    .finite()
    .gte(0, "No puede ser negativo"),
});
const onlyDieselSchema = fuelControlDetailBaseSchemaObject.merge(
  onlyDieselSchemaObject
);
const allOptionalSchema = fuelControlDetailBaseSchemaObject
  .merge(onlyHourometerSchemaObject.partial())
  .merge(onlyDieselSchemaObject.partial())
  .partial();
const [onlyHourometerSchema, genericSchema] = [
  fuelControlDetailBaseSchemaObject
    .merge(onlyHourometerSchemaObject)
    .merge(onlyDieselSchemaObject.partial()),
  fuelControlDetailBaseSchemaObject
    .merge(onlyHourometerSchemaObject)
    .merge(onlyDieselSchemaObject.partial()),
].map((schema) => {
  return schema
    .refine(
      (data) => {
        if (!data.finalHourometer || !data.initialHourometer) return true;
        return data.finalHourometer >= data.initialHourometer;
      },
      {
        message: "El horómetro final debe ser mayor al inicial",
        path: ["finalHourometer"],
      }
    )
    .superRefine((value, ctx) => {
      if (value.dieselHourometer && !value.diesel) {
        return ctx.addIssue({
          message: "Debes ingresar los litros de diesel",
          path: ["diesel"],
          code: "custom",
        });
      }
      if (!value.diesel) return;
      if (!value.dieselHourometer) {
        return ctx.addIssue({
          message: "Debes ingresar los horometros de carga de diesel",
          path: ["dieselHourometer"],
          code: "custom",
        });
      }
      if (
        value.dieselHourometer < value.initialHourometer ||
        value.dieselHourometer > value.finalHourometer
      ) {
        return ctx.addIssue({
          message:
            "El horómetro de carga debe estar entre el horómetro inicial y final",
          path: ["dieselHourometer"],
          code: "custom",
        });
      }
      if (!value.dieselLastHourometer) {
        return ctx.addIssue({
          message: "Debes ingresar los horometros de carga de diesel",
          path: ["dieselLastHourometer"],
          code: "custom",
        });
      }
      if (value.dieselHourometer - value.dieselLastHourometer <= 0) {
        return ["dieselHourometer", "dieselLastHourometer"].forEach((path) => {
          ctx.addIssue({
            message: "El horómetro de carga debe ser mayor al anterior",
            path: [path],
            code: "custom",
          });
        });
      }
    });
});

type FuelControlDetailSchema = z.infer<
  typeof fuelControlDetailBaseSchemaObject
> &
  z.infer<typeof onlyHourometerSchemaObject> &
  z.infer<typeof onlyDieselSchemaObject>;

interface FuelReportFormProps {
  close: () => void;
  detail?: Partial<FuelControlDetailSchema>;
}
export const FuelReportForm = (props: FuelReportFormProps) => {
  const params = useParams();
  const { type = "GENERIC", ...detail } = props.detail ?? {};
  const { data } = useQuery(getFuelControlOptionsQuery);
  const schema =
    type === "ONLY_DIESEL"
      ? onlyDieselSchema
      : type === "ONLY_HOUROMETER"
      ? onlyHourometerSchema
      : genericSchema;
  const { control, handleSubmit, watch } = useForm<FuelControlDetailSchema>({
    resolver: zodResolver(detail.id === undefined ? allOptionalSchema : schema),
    defaultValues: detail,
  });
  const [addFuelControlDetail, { loading: isAdding }] = useMutation(
    addFuelControlDetailMutation
  );

  const [removeFuelControlDetail, { loading: isRemoving }] = useMutation(
    removeFuelControlDetailMutation,
    {
      variables: {
        params: { id: props.detail?.id ?? -1 },
      },
    }
  );
  const [updateFuelControlDetail, { loading: isUpdating }] = useMutation(
    updateFuelControlDetailMutation
  );

  const handleClose = () => {
    props.close();
  };
  const previousDetail = Boolean((props.detail?.id ?? 0) > 0);
  const handleRemove = async () => {
    await removeFuelControlDetail();
    props.close();
  };
  const onSubmit = async (data: FuelControlDetailSchema) => {
    const dataToSend = Object.fromEntries(
      Object.entries(data).filter(
        ([key]) => !["id", "hours", "performance", "type"].includes(key)
      )
    ) as VariablesOf<typeof addFuelControlDetailMutation>["params"];
    if (previousDetail) {
      await updateFuelControlDetail({
        variables: {
          params: {
            ...dataToSend,
            id: data.id ?? 0,
          },
        },
      });
    } else {
      await addFuelControlDetail({
        variables: {
          params: {
            ...dataToSend,
            reportId: +params.id!,
          },
        },
      });
    }
    handleClose();
  };

  const [
    diesel,
    dieselHourometer,
    initialHourometer,
    dieselLastHourometer,
    resourceName,
  ] = watch([
    "diesel",
    "dieselHourometer",
    "initialHourometer",
    "dieselLastHourometer",
    "resourceName",
  ]);
  console.log({ data });
  return (
    <>
      <Content>
        <Typography variant="h6">Agregar Equipo</Typography>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {props.detail?.id ? (
            <Typography fontWeight={800}>
              Equipo:{" "}
              <Typography component="span" display={"inline"}>
                {props.detail?.resourceName}
              </Typography>
            </Typography>
          ) : (
            <Controller
              name="resourceName"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={
                    data?.fuelControlOptions.map((option) => option.name) ?? []
                  }
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      label="Equipo"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                ></Autocomplete>
              )}
            />
          )}
          <Controller
            name="resourceDetail"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={
                  data?.fuelControlOptions.find(
                    (option) => option.name === resourceName
                  )?.codes ?? []
                }
                onChange={(_, value) => {
                  field.onChange(value);
                }}
                value={field.value}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    label="Identificador"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              ></Autocomplete>
            )}
          />
          {["GENERIC", "ONLY_HOUROMETER"].includes(type) && (
            <>
              {props.detail?.dieselLastHourometer ? (
                <Typography fontWeight={800}>
                  Horómetro Inicial:{" "}
                  <Typography component="span" display={"inline"}>
                    {initialHourometer}
                  </Typography>
                </Typography>
              ) : (
                <Controller
                  name="initialHourometer"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      label="Horómetro inicial"
                      fullWidth
                      type="number"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      {...field}
                    ></TextField>
                  )}
                />
              )}
              <Controller
                name="finalHourometer"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Horómetro final"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...field}
                  ></TextField>
                )}
              />
            </>
          )}
          {["GENERIC", "ONLY_DIESEL"].includes(type) && (
            <Controller
              name="diesel"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  label="Diesel (lt)"
                  fullWidth
                  type="number"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...field}
                ></TextField>
              )}
            />
          )}
          {["GENERIC"].includes(type) && (
            <>
              <Controller
                name="dieselHourometer"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Horómetro carga diesel"
                    fullWidth
                    type="number"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...field}
                  ></TextField>
                )}
              />
              {props.detail?.dieselLastHourometer ? (
                <Typography fontWeight={800}>
                  Horómetro Anterior:{" "}
                  <Typography component="span" display={"inline"}>
                    {props.detail?.dieselLastHourometer}
                  </Typography>
                </Typography>
              ) : (
                <Controller
                  name="dieselLastHourometer"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      label="Horómetro anterior"
                      fullWidth
                      type="number"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      {...field}
                    ></TextField>
                  )}
                />
              )}
              <Typography fontWeight={800}>
                Horas:{" "}
                <Typography component="span" display={"inline"}>
                  {dieselLastHourometer && dieselHourometer
                    ? dieselHourometer - dieselLastHourometer
                    : "Por calcular"}
                </Typography>
              </Typography>
              <Typography fontWeight={800}>
                Rendimiento:{" "}
                <Typography component="span" display={"inline"}>
                  {diesel && dieselLastHourometer && dieselHourometer
                    ? Math.round(
                        (100 * diesel) /
                          (dieselHourometer - dieselLastHourometer)
                      ) /
                        100 +
                      " lt/hr"
                    : "Por calcular"}
                </Typography>
              </Typography>
            </>
          )}
        </Form>
      </Content>
      <DialogActions sx={{ padding: "8px 24px" }}>
        {previousDetail ? (
          <>
            <Button onClick={handleRemove} color="error" disabled={isRemoving}>
              ELIMINAR
              {isRemoving && (
                <CircularProgress sx={{ marginLeft: 1 }} size={20} />
              )}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="info"
              disabled={isUpdating}
            >
              GUARDAR{" "}
              {isUpdating && (
                <CircularProgress sx={{ marginLeft: 1 }} size={20} />
              )}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} color="error">
              CANCELAR
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="info"
              disabled={isAdding}
            >
              GUARDAR{" "}
              {isAdding && (
                <CircularProgress sx={{ marginLeft: 1 }} size={20} />
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
};
